import React, { createContext, useContext, useMemo } from "react";
import { AdminFeatureId, resolveAdminFeatures } from "@equipped/core/admin/adminFeatures";
import type { AdminCapabilityId } from "@equipped/core/admin/adminCapabilities";
import { ADMIN_FEATURE_CONFIG } from "../config/adminFeatures.config";
import { useProvider } from "../providers/ProviderContext";

/**
 * Wires docs/358-ADMIN-FEATURE-CONFIGURATION.md's pure `resolveAdminFeatures` into
 * React — the ONE place the Mantis admin resolves "what is this deployment allowed to
 * RENDER." v0 resolves from the build-time {@link ADMIN_FEATURE_CONFIG} only —
 * `GET /provider` does not yet emit a served `capabilities` list (docs/344 is still a
 * plan, not built), so the resolver's second, subtract-only argument is simply omitted
 * here; wiring it in later (docs/358 §9 v1) needs no reshaping of this provider.
 */
export interface AdminFeaturesState {
  features: ReadonlySet<AdminFeatureId>;
  /** Whether this deployment renders the given feature — the ONE gate both nav
   * (`DrawerContent`) and route (`App.tsx`) filtering read, plus any future
   * action-level gating (docs/358 §4.4 — hide a button/column). */
  has: (id: AdminFeatureId) => boolean;
  /** Active-provider contract gate. A null result is the unchanged single-provider mode. */
  hasCapability: (id: AdminCapabilityId) => boolean;
}

const AdminFeaturesContext = createContext<AdminFeaturesState | null>(null);

export function AdminFeaturesProvider({ children }: { children: React.ReactNode }) {
  const { capabilities } = useProvider();
  const features = useMemo(() => resolveAdminFeatures(ADMIN_FEATURE_CONFIG), []);
  const value = useMemo<AdminFeaturesState>(
    () => ({
      features,
      has: (id: AdminFeatureId) => features.has(id),
      hasCapability: (id: AdminCapabilityId) => capabilities?.supported.has(id) ?? true,
    }),
    [capabilities, features],
  );
  return <AdminFeaturesContext.Provider value={value}>{children}</AdminFeaturesContext.Provider>;
}

export function useAdminFeatures(): AdminFeaturesState {
  const ctx = useContext(AdminFeaturesContext);
  if (!ctx) throw new Error("useAdminFeatures() must be used within <AdminFeaturesProvider>");
  return ctx;
}
